/**
 * 이상한 방 - 가구 그룹 레시피 (관계 기반 배치)
 *
 * 근거 문서: `이상한방-상태모델-v0.1.md` 3절(좌표)·3.1절(셀 점유) · 컨셉시트 4.1절(템플릿 셸)
 *
 * ## 이 파일이 존재하는 이유 - 정돈이 곧 게임 메카닉이다
 *
 * 이전 배치기는 태그와 셀 충돌만 보고 오브젝트를 **개별로** 흩뿌렸다. 규칙 위반은 하나도 없었는데
 * 실측 평가는 "중구난방이라 뭘 외워야 할지 모르겠다"였다. 원인은 밀도가 아니라 **구조의 부재**다.
 *
 *  - 사람은 개별 물체가 아니라 **패턴에서 벗어난 것**을 찾는다. 패턴이 없으면 기준선이 없다.
 *  - 기억은 **의미 단위(청크)**로 형성된다. "침대 옆 협탁, 그 위에 스탠드"는 1청크지만
 *    "물건 379개"는 379청크이고, 시각 작업기억은 4~7청크에서 끊긴다.
 *    청크화가 안 되면 외우기 페이즈가 통째로 무의미해진다 - 외울 수 없는 것은 비교할 수도 없다.
 *
 * Exit 8의 지하철 통로가 완벽히 규칙적인 것은 미술 취향이 아니라 이 이유다.
 *
 * ## 그래서 배치의 단위를 바꾼다
 *
 * 좌표를 뽑아 놓는 대신 **관계를 선언**한다: 침대는 벽에 등을 붙이고, 협탁은 침대 옆에,
 * 스탠드는 협탁 위에. 앵커 하나가 놓이면 나머지는 그 관계에서 유도된다.
 * 그 결과가 "규칙적인 방"이고, 규칙적인 방에서만 체인저의 변화가 **어긋남**으로 읽힌다.
 *
 * ## 레시피는 코드가 아니라 데이터다
 *
 * 관계 술어(`Relation`)와 그것을 좌표로 푸는 해석기만 코드이고, "침실은 이렇게 생겼다"는
 * 전부 `HOME_RECIPES` 데이터다. 나중에 사파리·우주 맵이 추가되면 같은 해석기에 다른 레시피를
 * 얹는다(`roomGen`의 `recipes` 옵션). 절차로 하드코딩하면 맵 종류마다 배치기를 새로 짜게 된다.
 *
 * ## 회전 규약 (여기서 한 번만 정한다)
 *
 * `rot 0 = 정면이 +z`다. `render/objectView.ts`의 방향 표식(`MARKER_DIRECTIONS[0] = +z`)과
 * `meshFactory.ts`의 형상 규약이 이미 그렇게 잡혀 있고, Three.js의 Y축 회전이 +z를 +x로 보내므로
 * `rot 1 = +x`, `rot 2 = -z`, `rot 3 = -x`가 자동으로 따라온다.
 * **이 파일의 모든 관계는 이 한 줄에서 유도된다** - 벽에 등을 붙인다는 것은
 * "벽 반대쪽을 바라보는 rot을 고른다"는 뜻이고, 마주본다는 것은 "상대 쪽 rot을 고른다"는 뜻이다.
 */

import { ROT_HALF_TURN, ROT_STEP_COUNT, type CatalogId, type Cell3, type Rot } from './types'
import type { CellRect } from './floorPlan'
import { rotatedFootprint } from './grid'
import type { Rng } from './rng'

/* --- 방위와 회전 ------------------------------------------------------------- */

/** 구획 사각형의 네 변. `floorPlan.ts`의 `RoomSide`와 같은 규약이다(+z가 북). */
export type WallSide = 'south' | 'north' | 'west' | 'east'

export const WALL_SIDES: readonly WallSide[] = ['south', 'north', 'west', 'east']

/**
 * 그 면에 **등을 붙였을 때** 안쪽을 바라보는 회전. 이름이 가리키는 것은 물체가 향하는 쪽이 아니라
 * **물체가 등지는 쪽**이다 - 동쪽 벽(최대 x)에 붙으면 -x를 보므로 rot 3이다.
 *
 * `around`도 같은 표를 쓴다: 앵커의 동쪽에 놓인 의자는 앵커 쪽(-x)을 봐야 하므로 값이 같다.
 */
export const WALL_FACING_ROT: Readonly<Record<WallSide, Rot>> = {
  south: 0,
  west: 1,
  north: 2,
  east: 3,
}

/** rot → 정면 단위 벡터. `Record`로 적은 이유는 배열 인덱싱이 `undefined`를 낳기 때문이다. */
const FACING_VECTORS: Readonly<Record<Rot, Readonly<{ x: number; z: number }>>> = {
  0: { x: 0, z: 1 },
  1: { x: 1, z: 0 },
  2: { x: 0, z: -1 },
  3: { x: -1, z: 0 },
}

// 회전 단계 수는 `core/types.ROT_STEP_COUNT`·`ROT_HALF_TURN` 하나다 - 한때 네 파일이 `4`를, 두 파일이 `2`를 각자 들고 있었다(P1 정리).

export function facingVector(rot: Rot): Readonly<{ x: number; z: number }> {
  return FACING_VECTORS[rot]
}

/** 180° 반대 회전. `facing` 관계가 "앵커를 마주본다"를 만들 때 쓴다. */
export function oppositeRot(rot: Rot): Rot {
  return ((rot + ROT_HALF_TURN) % ROT_STEP_COUNT) as Rot
}

/**
 * (dx, dz) 방향을 바라보는 회전. 축이 네 개뿐이므로 **우세한 축**으로 반올림한다.
 * 대각선에서 x를 먼저 보는 것은 임의 선택이지만 고정이어야 결정론이 유지된다.
 */
export function rotToward(dx: number, dz: number): Rot {
  if (Math.abs(dx) > Math.abs(dz)) return dx > 0 ? 1 : 3
  return dz >= 0 ? 0 : 2
}

/* --- 배치 후보 --------------------------------------------------------------- */

/**
 * 관계가 제안하는 좌표 하나. 생성기는 이 목록을 **순서대로** 시도하고 첫 성공에서 멈춘다.
 * `wallSide`는 벽에 붙은 배치에만 값이 있으며, 나중 슬롯의 `alongWall`·`wallMounted`가 되짚는다.
 */
export type Candidate = Readonly<{ cell: Cell3; rot: Rot; wallSide: WallSide | null }>

/** 관계가 참조하는, 이미 놓인 배치. `size`는 회전이 적용된 점유 크기다. */
export type GroupAnchor = Readonly<{
  cell: Cell3
  size: Cell3
  rot: Rot
  wallSide: WallSide | null
}>

/** 그룹 안에서의 역할 이름. 관계는 좌표가 아니라 이 이름으로 서로를 참조한다. */
export type SlotRole = string

/**
 * 배치 술어. 각 항목이 "이 소품은 어디에 있어야 하는가"를 한 줄로 말한다.
 *
 *  - `againstWall` - 벽에 등을 붙임(침대·소파·책장·냉장고). 회전이 벽 방향에서 결정된다
 *  - `alongWall`   - 앵커와 **같은 벽**을 따라 이어 붙임(싱크대-스토브-냉장고)
 *  - `beside`      - 앵커 옆(침대 옆 협탁). 회전은 앵커를 따라간다
 *  - `inFront`     - 앵커 정면 방향(침대 앞 러그). 회전은 앵커를 따라간다
 *  - `facing`      - 앵커 정면 방향에 두되 **앵커를 마주봄**(소파를 향한 TV). 회전이 여기서 결정된다
 *  - `around`      - 앵커 둘레(식탁 둘레 의자). 전부 앵커를 마주본다
 *  - `onSurface`   - 앵커 상판 위(테이블 위 컵). 3절대로 부모-자식이 아니라 y가 높은 셀일 뿐이다
 *  - `center`/`corner` - 구획 중앙 / 모서리
 *  - `wallMounted` - 벽에 걺(머리 위 높이). `of`를 주면 그 가구 **바로 위**에 건다(세면대 위 거울)
 *  - `ceiling`     - 천장에 매닮
 */
export type Relation =
  | Readonly<{ kind: 'againstWall' }>
  | Readonly<{ kind: 'alongWall'; of: SlotRole }>
  | Readonly<{ kind: 'beside'; of: SlotRole; gap?: number }>
  | Readonly<{ kind: 'inFront'; of: SlotRole; gap?: number }>
  | Readonly<{ kind: 'facing'; of: SlotRole; gap?: number }>
  | Readonly<{ kind: 'around'; of: SlotRole; gap?: number }>
  | Readonly<{ kind: 'onSurface'; of: SlotRole }>
  | Readonly<{ kind: 'center' }>
  | Readonly<{ kind: 'corner' }>
  | Readonly<{ kind: 'wallMounted'; of?: SlotRole }>
  | Readonly<{ kind: 'ceiling' }>

/** 관계가 참조 대상을 요구하는가. 요구하는데 없으면 그 슬롯은 통째로 건너뛴다. */
export function relationAnchorRole(relation: Relation): SlotRole | null {
  return 'of' in relation && relation.of !== undefined ? relation.of : null
}

/* --- 소품 선택 --------------------------------------------------------------- */

/**
 * 카탈로그 등급 이름. `catalog/entryTypes.ts`의 `SizeClass`와 같은 문자열이지만
 * **import하지 않는다** - 코어가 카탈로그 레이어를 import하면 의존이 뒤집히고,
 * 무엇보다 이 모듈은 아무 카탈로그(테스트 픽스처 포함)와도 굴러가야 한다.
 */
export type SizeClassName = 'anchor' | 'floor' | 'surface' | 'wall' | 'ceiling'

/**
 * 슬롯이 어떤 소품을 부를 것인가.
 *
 * `ids`가 먼저다 - 레시피는 "침대"라고 말해야 읽히지 "면적 40 이상의 anchor"라고 말하면 안 읽힌다.
 * 다만 카탈로그는 갈아끼울 수 있는 데이터이므로, `ids`가 하나도 남지 않으면(다른 카탈로그·픽스처)
 * `sizeClass` 풀로 물러난다. 물러날 곳조차 없으면 그 슬롯은 비어 있는 채로 넘어간다.
 */
export type EntrySelector = Readonly<{
  ids?: readonly CatalogId[]
  sizeClass?: SizeClassName
}>

/** 그룹의 슬롯 하나. `min`을 못 채우면 그룹 전체가 무효가 된다(생성기가 되돌린다). */
export type GroupSlot = Readonly<{
  role: SlotRole
  select: EntrySelector
  relation: Relation
  min: number
  max: number
}>

/**
 * 가구 그룹 하나. **`slots[0]`이 앵커**이며 관계 참조 없이 놓을 수 있어야 한다.
 *
 *  - `priority` - 낮을수록 먼저. 0은 방의 성격을 만드는 주가구, 1~2는 장식·채움
 *  - `maxPerZone` - 한 구획에 몇 번까지. 이 상한이 "침대 네 개짜리 침실"을 막는 유일한 장치다
 */
export type GroupRecipe = Readonly<{
  id: string
  priority: number
  maxPerZone: number
  slots: readonly GroupSlot[]
}>

/** 구획 태그 → 그 구획에 놓을 수 있는 그룹들. 맵 종류를 바꾼다는 것은 이 책을 바꾼다는 뜻이다. */
export type RecipeBook = ReadonlyMap<string, readonly GroupRecipe[]>

/* --- 탐색 상수 --------------------------------------------------------------- */

/** 벽 한 면에서 뽑아 볼 위치 수. 네 면 합쳐 32개면 빈자리 찾기에 충분하고 셔플 비용도 작다. */
const WALL_POSITIONS_PER_SIDE = 8
/** `inFront`·`facing`이 간격을 넓혀 가며 재시도하는 횟수. 앞이 막혔으면 조금 더 멀리 둔다. */
const FRONT_GAP_SEARCH = 6
/** 정면 배치의 좌우 어긋남 허용치(셀). 0(정중앙)부터 가까운 순으로 시도한다. */
const FRONT_LATERAL_SPREAD = 3
/** 가구 **바로 위**에 거는 벽걸이의 좌우 어긋남 허용치. 크게 잡으면 거울이 세면대 옆으로 비켜난다. */
const MOUNT_LATERAL_SPREAD = 1
/** `beside`가 앵커 등선에서 앞으로 밀어 볼 칸 수. 협탁이 침대 머리맡에서 크게 벗어나면 안 된다. */
const BESIDE_ALIGN_SPREAD = 2
/** `alongWall`이 앵커와 벌려 볼 최대 간격. 0이 기본이고 막히면 한두 칸 띄운다. */
const ALONG_WALL_GAP_SEARCH = 2
/** 한 변에서 뽑을 `around` 후보 수. 식탁 한 변에 의자 셋이면 충분하다. */
const AROUND_POSITIONS_PER_SIDE = 3
/** `center`가 중앙에서 벗어나 볼 최대 거리(체비셰프). 통로 예약이 중앙을 지나므로 여유가 필요하다. */
const CENTER_MAX_RING = 10
/** 중앙에서 같은 거리인 후보를 몇 개까지 담을지. 이 상한이 탐색을 바깥 고리까지 밀어낸다. */
const CENTER_CANDIDATES_PER_RING = 4
/** 천장·상판처럼 후보가 면적에 비례해 폭증하는 관계의 상한. */
const MAX_AREA_CANDIDATES = 40

/* --- 순수 기하 헬퍼 ---------------------------------------------------------- */

function fitsZone(zone: CellRect, cell: Cell3, size: Cell3): boolean {
  return (
    cell.x >= zone.x &&
    cell.z >= zone.z &&
    cell.x + size.x <= zone.x + zone.sizeX &&
    cell.z + size.z <= zone.z + zone.sizeZ
  )
}

/** 앵커 구간(min, 길이)의 한가운데에 size를 놓았을 때의 최소 코너. */
function centerAlign(anchorMin: number, anchorSize: number, size: number): number {
  return anchorMin + Math.floor((anchorSize - size) / 2)
}

/** 0, +1, -1, +2, -2 … 순서의 오프셋. 정중앙을 먼저 시도하려는 관계가 공유한다. */
function centeredOffsets(spread: number): readonly number[] {
  const offsets: number[] = [0]
  for (let step = 1; step <= spread; step += 1) offsets.push(step, -step)
  return offsets
}

/** [min, max] 구간의 정수를 `preferred`에서 가까운 순으로. 비면 배치가 불가능하다는 뜻이다. */
function spanPositions(min: number, max: number, preferred: number): readonly number[] {
  if (min > max) return []
  const positions: number[] = []
  for (let value = min; value <= max; value += 1) positions.push(value)
  return positions.sort((a, b) => Math.abs(a - preferred) - Math.abs(b - preferred))
}

/** 여러 목록을 한 개씩 번갈아 꺼낸다. `around`가 네 변에 의자를 고루 돌리는 장치다. */
function interleave<T>(lists: readonly (readonly T[])[]): readonly T[] {
  const merged: T[] = []
  const longest = Math.max(0, ...lists.map((list) => list.length))
  for (let index = 0; index < longest; index += 1) {
    for (const list of lists) {
      const item = list[index]
      if (item !== undefined) merged.push(item)
    }
  }
  return merged
}

/** 회전 후보. 대칭 소품은 어차피 화면이 같으므로 네 방향을 다 열어 두고 순서만 섞는다. */
function shuffledRots(rng: Rng): readonly Rot[] {
  return rng.shuffle([0, 1, 2, 3] as readonly Rot[])
}

/* --- 관계별 후보 생성 --------------------------------------------------------- */

/** 한 벽면에 등을 붙인 후보들. `baseY`가 부착 바닥 높이다(벽면물은 머리 위). */
function wallSideCandidates(
  zone: CellRect,
  footprint: Cell3,
  side: WallSide,
  baseY: number,
  rng: Rng,
): readonly Candidate[] {
  const rot = WALL_FACING_ROT[side]
  const size = rotatedFootprint(footprint, rot)
  if (size.x > zone.sizeX || size.z > zone.sizeZ) return []

  const maxX = zone.x + zone.sizeX - size.x
  const maxZ = zone.z + zone.sizeZ - size.z
  const candidates: Candidate[] = []

  if (side === 'south' || side === 'north') {
    const z = side === 'south' ? zone.z : maxZ
    for (let x = zone.x; x <= maxX; x += 1) candidates.push({ cell: { x, y: baseY, z }, rot, wallSide: side })
  } else {
    const x = side === 'west' ? zone.x : maxX
    for (let z = zone.z; z <= maxZ; z += 1) candidates.push({ cell: { x, y: baseY, z }, rot, wallSide: side })
  }

  return rng.shuffle(candidates).slice(0, WALL_POSITIONS_PER_SIDE)
}

/** 네 벽 전부. 벽 순서를 섞어 같은 방에서 매번 같은 벽만 쓰이는 것을 막는다. */
function againstWallCandidates(
  zone: CellRect,
  footprint: Cell3,
  baseY: number,
  rng: Rng,
): readonly Candidate[] {
  return rng
    .shuffle(WALL_SIDES)
    .flatMap((side) => wallSideCandidates(zone, footprint, side, baseY, rng))
}

/** 앵커와 같은 벽을 따라 이어 붙인다. 주방 조리대(싱크대-스토브-냉장고)가 이 관계로 만들어진다. */
function alongWallCandidates(
  zone: CellRect,
  footprint: Cell3,
  anchor: GroupAnchor,
  baseY: number,
  rng: Rng,
): readonly Candidate[] {
  const side = anchor.wallSide
  if (side === null) return []

  const rot = WALL_FACING_ROT[side]
  const size = rotatedFootprint(footprint, rot)
  const isAlongX = side === 'south' || side === 'north'
  // 깊이 좌표는 앵커를 따라가지 않는다 - 깊이가 다른 가구도 **벽에는 나란히** 붙어야 한다.
  const depth = isAlongX
    ? side === 'south'
      ? zone.z
      : zone.z + zone.sizeZ - size.z
    : side === 'west'
      ? zone.x
      : zone.x + zone.sizeX - size.x

  const anchorMin = isAlongX ? anchor.cell.x : anchor.cell.z
  const anchorMax = anchorMin + (isAlongX ? anchor.size.x : anchor.size.z) - 1
  const alongSize = isAlongX ? size.x : size.z
  const candidates: Candidate[] = []

  for (const direction of rng.shuffle([1, -1] as readonly number[])) {
    for (let gap = 0; gap <= ALONG_WALL_GAP_SEARCH; gap += 1) {
      const along = direction > 0 ? anchorMax + 1 + gap : anchorMin - gap - alongSize
      const cell: Cell3 = isAlongX
        ? { x: along, y: baseY, z: depth }
        : { x: depth, y: baseY, z: along }
      if (fitsZone(zone, cell, size)) candidates.push({ cell, rot, wallSide: side })
    }
  }

  return candidates
}

/** 앵커 옆(정면 벡터에 수직인 방향). 회전은 앵커를 따라가므로 둘이 같은 쪽을 본다. */
function besideCandidates(
  zone: CellRect,
  footprint: Cell3,
  anchor: GroupAnchor,
  gap: number,
  baseY: number,
  rng: Rng,
): readonly Candidate[] {
  const rot = anchor.rot
  const size = rotatedFootprint(footprint, rot)
  const facing = facingVector(rot)
  const isSideAxisX = facing.z !== 0

  const anchorMinX = anchor.cell.x
  const anchorMaxX = anchorMinX + anchor.size.x - 1
  const anchorMinZ = anchor.cell.z
  const anchorMaxZ = anchorMinZ + anchor.size.z - 1
  const candidates: Candidate[] = []

  for (const direction of rng.shuffle([1, -1] as readonly number[])) {
    for (const offset of centeredOffsets(BESIDE_ALIGN_SPREAD)) {
      let cell: Cell3
      if (isSideAxisX) {
        const x = direction > 0 ? anchorMaxX + 1 + gap : anchorMinX - gap - size.x
        // 등선 정렬: 앵커의 뒷면과 같은 선에서 시작해 앞쪽으로만 어긋난다.
        const backZ = facing.z > 0 ? anchorMinZ : anchorMaxZ + 1 - size.z
        cell = { x, y: baseY, z: backZ + offset * facing.z }
      } else {
        const z = direction > 0 ? anchorMaxZ + 1 + gap : anchorMinZ - gap - size.z
        const backX = facing.x > 0 ? anchorMinX : anchorMaxX + 1 - size.x
        cell = { x: backX + offset * facing.x, y: baseY, z }
      }
      if (fitsZone(zone, cell, size)) candidates.push({ cell, rot, wallSide: null })
    }
  }

  return candidates
}

/**
 * 앵커 **정면 방향**에 놓는다. `rot`을 밖에서 받는 것이 `inFront`와 `facing`의 유일한 차이다 -
 * 앵커와 같은 방향을 보면 나란한 배치(소파 앞 커피테이블)이고, 반대면 마주보는 배치(TV)다.
 */
function frontCandidates(
  zone: CellRect,
  footprint: Cell3,
  anchor: GroupAnchor,
  rot: Rot,
  minGap: number,
  baseY: number,
): readonly Candidate[] {
  const size = rotatedFootprint(footprint, rot)
  const facing = facingVector(anchor.rot)
  const anchorMaxX = anchor.cell.x + anchor.size.x - 1
  const anchorMaxZ = anchor.cell.z + anchor.size.z - 1
  const candidates: Candidate[] = []

  for (let gap = minGap; gap <= minGap + FRONT_GAP_SEARCH; gap += 1) {
    for (const offset of centeredOffsets(FRONT_LATERAL_SPREAD)) {
      let cell: Cell3
      if (facing.z !== 0) {
        const z = facing.z > 0 ? anchorMaxZ + 1 + gap : anchor.cell.z - gap - size.z
        cell = { x: centerAlign(anchor.cell.x, anchor.size.x, size.x) + offset, y: baseY, z }
      } else {
        const x = facing.x > 0 ? anchorMaxX + 1 + gap : anchor.cell.x - gap - size.x
        cell = { x, y: baseY, z: centerAlign(anchor.cell.z, anchor.size.z, size.z) + offset }
      }
      if (fitsZone(zone, cell, size)) candidates.push({ cell, rot, wallSide: null })
    }
  }

  return candidates
}

/** 앵커 둘레. 네 변을 번갈아 내보내므로 의자 넷을 뽑으면 네 변에 하나씩 붙는다. */
function aroundCandidates(
  zone: CellRect,
  footprint: Cell3,
  anchor: GroupAnchor,
  gap: number,
  baseY: number,
): readonly Candidate[] {
  const anchorMaxX = anchor.cell.x + anchor.size.x - 1
  const anchorMaxZ = anchor.cell.z + anchor.size.z - 1

  const perSide = WALL_SIDES.map((side): readonly Candidate[] => {
    // 앵커를 마주보는 회전. "그 면을 등진다"와 같은 표라서 벽 표를 그대로 쓴다.
    const rot = WALL_FACING_ROT[side]
    const size = rotatedFootprint(footprint, rot)
    const isAlongX = side === 'south' || side === 'north'

    const fixed = isAlongX
      ? side === 'north'
        ? anchorMaxZ + 1 + gap
        : anchor.cell.z - gap - size.z
      : side === 'east'
        ? anchorMaxX + 1 + gap
        : anchor.cell.x - gap - size.x

    const spanMin = isAlongX ? anchor.cell.x : anchor.cell.z
    const spanMax = (isAlongX ? anchorMaxX : anchorMaxZ) - (isAlongX ? size.x : size.z) + 1
    const preferred = isAlongX
      ? centerAlign(anchor.cell.x, anchor.size.x, size.x)
      : centerAlign(anchor.cell.z, anchor.size.z, size.z)

    return spanPositions(spanMin, spanMax, preferred)
      .slice(0, AROUND_POSITIONS_PER_SIDE)
      .map((along): Candidate => ({
        cell: isAlongX ? { x: along, y: baseY, z: fixed } : { x: fixed, y: baseY, z: along },
        rot,
        wallSide: null,
      }))
      .filter((candidate) => fitsZone(zone, candidate.cell, size))
  })

  return interleave(perSide)
}

/**
 * 앵커 상판 위. 3절의 "책상 위 머그컵 = 책상보다 y가 높은 셀" 단순화를 그대로 쓴다.
 * **상판 안에 완전히 들어가는 것만** 후보로 낸다 - 걸치면 화면에서 공중에 뜬 물건이 된다.
 */
function onSurfaceCandidates(
  footprint: Cell3,
  anchor: GroupAnchor,
  ceilingY: number,
  rng: Rng,
): readonly Candidate[] {
  const topY = anchor.cell.y + anchor.size.y
  const candidates: Candidate[] = []

  for (const rot of shuffledRots(rng)) {
    const size = rotatedFootprint(footprint, rot)
    if (size.x > anchor.size.x || size.z > anchor.size.z) continue
    if (topY + size.y > ceilingY) continue

    for (let x = anchor.cell.x; x + size.x <= anchor.cell.x + anchor.size.x; x += 1) {
      for (let z = anchor.cell.z; z + size.z <= anchor.cell.z + anchor.size.z; z += 1) {
        candidates.push({ cell: { x, y: topY, z }, rot, wallSide: null })
      }
    }
  }

  return rng.shuffle(candidates).slice(0, MAX_AREA_CANDIDATES)
}

/**
 * 구획 중앙에서 가까운 순. 식탁·러그처럼 방 한가운데를 차지해야 하는 것들이 쓴다.
 *
 * **고리마다 개수를 묶는 것**이 이 함수의 요점이다. 묶지 않으면 고리 하나가 후보 상한을 다 먹어
 * 중심 ±2칸만 탐색하게 되는데, 하필 통로 예약이 구획 중심을 관통하므로(문 → 중심 L자)
 * 식탁이 **항상** 실패한다. 실제로 주방에 식탁이 없는 방이 그렇게 나왔다.
 */
function centerCandidates(
  zone: CellRect,
  footprint: Cell3,
  baseY: number,
  rng: Rng,
): readonly Candidate[] {
  const rots = shuffledRots(rng)
  const candidates: Candidate[] = []

  for (let ring = 0; ring <= CENTER_MAX_RING; ring += 1) {
    const ofRing: Candidate[] = []

    for (const rot of rots) {
      const size = rotatedFootprint(footprint, rot)
      const baseX = centerAlign(zone.x, zone.sizeX, size.x)
      const baseZ = centerAlign(zone.z, zone.sizeZ, size.z)

      for (const offsetX of centeredOffsets(ring)) {
        for (const offsetZ of centeredOffsets(ring)) {
          if (Math.max(Math.abs(offsetX), Math.abs(offsetZ)) !== ring) continue
          const cell: Cell3 = { x: baseX + offsetX, y: baseY, z: baseZ + offsetZ }
          if (fitsZone(zone, cell, size)) ofRing.push({ cell, rot, wallSide: null })
        }
      }
    }

    candidates.push(...rng.shuffle(ofRing).slice(0, CENTER_CANDIDATES_PER_RING))
    if (candidates.length >= MAX_AREA_CANDIDATES) break
  }

  return candidates
}

/** 네 모서리. 두 벽이 만나는 자리라 화분·플로어램프처럼 방 가장자리를 채우는 소품의 자리다. */
function cornerCandidates(
  zone: CellRect,
  footprint: Cell3,
  baseY: number,
  rng: Rng,
): readonly Candidate[] {
  const corners: readonly (readonly [WallSide, WallSide])[] = [
    ['west', 'south'],
    ['east', 'south'],
    ['west', 'north'],
    ['east', 'north'],
  ]

  return rng.shuffle(corners).flatMap(([sideX, sideZ]) =>
    rng.shuffle([sideX, sideZ] as readonly WallSide[]).flatMap((facingSide): readonly Candidate[] => {
      const rot = WALL_FACING_ROT[facingSide]
      const size = rotatedFootprint(footprint, rot)
      const cell: Cell3 = {
        x: sideX === 'west' ? zone.x : zone.x + zone.sizeX - size.x,
        y: baseY,
        z: sideZ === 'south' ? zone.z : zone.z + zone.sizeZ - size.z,
      }
      return fitsZone(zone, cell, size) ? [{ cell, rot, wallSide: facingSide }] : []
    }),
  )
}

/**
 * 벽걸이. 바닥 셀을 한 칸도 쓰지 않으므로 통로 위에도 걸 수 있다 -
 * 이것은 예외 규칙이 아니라 **부착 높이가 머리 위라는 기하**에서 나온다.
 * `of`를 주면 그 가구가 붙은 벽의, 그 가구 바로 위로 좁힌다(세면대 위 거울).
 */
function wallMountedCandidates(
  zone: CellRect,
  footprint: Cell3,
  anchor: GroupAnchor | null,
  mountY: number,
  ceilingY: number,
  rng: Rng,
): readonly Candidate[] {
  const inCeiling = (candidate: Candidate): boolean =>
    mountY + rotatedFootprint(footprint, candidate.rot).y <= ceilingY

  if (anchor === null || anchor.wallSide === null) {
    return againstWallCandidates(zone, footprint, mountY, rng).filter(inCeiling)
  }

  const side = anchor.wallSide
  const rot = WALL_FACING_ROT[side]
  const size = rotatedFootprint(footprint, rot)
  if (mountY + size.y > ceilingY) return []

  const isAlongX = side === 'south' || side === 'north'
  const depth = isAlongX
    ? side === 'south'
      ? zone.z
      : zone.z + zone.sizeZ - size.z
    : side === 'west'
      ? zone.x
      : zone.x + zone.sizeX - size.x
  const centered = isAlongX
    ? centerAlign(anchor.cell.x, anchor.size.x, size.x)
    : centerAlign(anchor.cell.z, anchor.size.z, size.z)

  return centeredOffsets(MOUNT_LATERAL_SPREAD)
    .map((offset): Cell3 =>
      isAlongX
        ? { x: centered + offset, y: mountY, z: depth }
        : { x: depth, y: mountY, z: centered + offset },
    )
    .filter((cell) => fitsZone(zone, cell, size))
    .map((cell): Candidate => ({ cell, rot, wallSide: side }))
}

/** 천장에 매단다. 최상단에 붙으므로 바닥에도 몸높이에도 닿지 않는다. */
function ceilingCandidates(
  zone: CellRect,
  footprint: Cell3,
  mountY: number,
  ceilingY: number,
  rng: Rng,
): readonly Candidate[] {
  const candidates: Candidate[] = []

  for (const rot of shuffledRots(rng)) {
    const size = rotatedFootprint(footprint, rot)
    const y = ceilingY - size.y
    if (y < mountY) continue

    for (let x = zone.x; x + size.x <= zone.x + zone.sizeX; x += 1) {
      for (let z = zone.z; z + size.z <= zone.z + zone.sizeZ; z += 1) {
        candidates.push({ cell: { x, y, z }, rot, wallSide: null })
      }
    }
  }

  return rng.shuffle(candidates).slice(0, MAX_AREA_CANDIDATES)
}

/** 관계 해석에 필요한 주변 정보. 구획 하나·소품 하나마다 새로 만든다. */
export type RelationContext = Readonly<{
  zone: CellRect
  footprint: Cell3
  /** 바닥 셀의 y. */
  floorY: number
  /** 벽걸이 부착 높이(머리 위). */
  mountY: number
  /** 천장 y = 방 높이. 상판·천장 후보가 뚫고 나가지 않도록 쓴다. */
  ceilingY: number
  /** 관계가 가리키는 이미 놓인 배치. 요구하는데 없으면 후보는 빈 목록이다. */
  anchor: GroupAnchor | null
  rng: Rng
}>

/**
 * 술어 하나를 좌표 후보 목록으로 푼다. **순서가 곧 선호도**이며 생성기는 앞에서부터 시도한다.
 * 무작위는 전부 `ctx.rng`를 거치므로 같은 시드는 같은 후보 순서를 만든다.
 */
export function resolveRelation(relation: Relation, ctx: RelationContext): readonly Candidate[] {
  const { zone, footprint, floorY, mountY, ceilingY, anchor, rng } = ctx

  switch (relation.kind) {
    case 'againstWall':
      return againstWallCandidates(zone, footprint, floorY, rng)
    case 'center':
      return centerCandidates(zone, footprint, floorY, rng)
    case 'corner':
      return cornerCandidates(zone, footprint, floorY, rng)
    case 'ceiling':
      return ceilingCandidates(zone, footprint, mountY, ceilingY, rng)
    case 'wallMounted':
      return wallMountedCandidates(zone, footprint, anchor, mountY, ceilingY, rng)
    case 'alongWall':
      return anchor === null ? [] : alongWallCandidates(zone, footprint, anchor, floorY, rng)
    case 'beside':
      return anchor === null
        ? []
        : besideCandidates(zone, footprint, anchor, relation.gap ?? 0, floorY, rng)
    case 'inFront':
      return anchor === null
        ? []
        : frontCandidates(zone, footprint, anchor, anchor.rot, relation.gap ?? 0, floorY)
    case 'facing':
      return anchor === null
        ? []
        : frontCandidates(zone, footprint, anchor, oppositeRot(anchor.rot), relation.gap ?? 0, floorY)
    case 'around':
      return anchor === null
        ? []
        : aroundCandidates(zone, footprint, anchor, relation.gap ?? 0, floorY)
    case 'onSurface':
      return anchor === null ? [] : onSurfaceCandidates(footprint, anchor, ceilingY, rng)
  }
}

/* --- 레시피 DSL -------------------------------------------------------------- */

const from = (ids: readonly CatalogId[], sizeClass?: SizeClassName): EntrySelector => ({ ids, sizeClass })

const slot = (
  role: SlotRole,
  select: EntrySelector,
  relation: Relation,
  min: number,
  max: number,
): GroupSlot => ({ role, select, relation, min, max })

const againstWall = (): Relation => ({ kind: 'againstWall' })
const alongWall = (of: SlotRole): Relation => ({ kind: 'alongWall', of })
const beside = (of: SlotRole, gap = 0): Relation => ({ kind: 'beside', of, gap })
const inFront = (of: SlotRole, gap = 0): Relation => ({ kind: 'inFront', of, gap })
const facing = (of: SlotRole, gap = 0): Relation => ({ kind: 'facing', of, gap })
const around = (of: SlotRole, gap = 0): Relation => ({ kind: 'around', of, gap })
const onSurface = (of: SlotRole): Relation => ({ kind: 'onSurface', of })
const atCenter = (): Relation => ({ kind: 'center' })
const atCorner = (): Relation => ({ kind: 'corner' })
const wallMounted = (of?: SlotRole): Relation => ({ kind: 'wallMounted', of })
const onCeiling = (): Relation => ({ kind: 'ceiling' })

/* --- 소품 풀 ----------------------------------------------------------------- */

/** 상판 소품. 태그가 안 맞는 것은 생성기가 구획별로 다시 거르므로 넉넉히 적어도 된다. */
const TABLE_PROPS: readonly CatalogId[] = [
  'mug', 'cup', 'cup-coffee', 'glass', 'glass-wine', 'book', 'bowl', 'plate', 'apple', 'cookie',
  'donut', 'muffin', 'banana', 'soda-bottle', 'cake', 'pizza-box', 'utensil-fork', 'utensil-spoon',
]
const KITCHEN_PROPS: readonly CatalogId[] = [
  'pot', 'pot-lid', 'pan', 'frying-pan', 'cutting-board', 'knife-block', 'bowl', 'bowl-cereal',
  'plate', 'plate-deep', 'bottle', 'bottle-ketchup', 'shaker-salt', 'pepper-mill', 'can', 'can-small',
  'carton', 'bread', 'loaf', 'toaster', 'blender', 'coffee-machine', 'microwave', 'honey',
  'peanut-butter', 'egg-cup', 'mortar', 'rolling-pin', 'whisk', 'watermelon', 'pumpkin', 'croissant',
]
const BEDSIDE_PROPS: readonly CatalogId[] = [
  'lamp-table', 'lamp-square-table', 'book', 'mug', 'glass', 'cup', 'soda-bottle', 'cookie', 'apple',
]
const DESK_PROPS: readonly CatalogId[] = [
  'monitor', 'keyboard', 'computer-mouse', 'laptop', 'book', 'mug', 'lamp-square-table', 'speaker-small',
]
const SHELF_PROPS: readonly CatalogId[] = [
  'book', 'speaker-small', 'lamp-table', 'radio', 'television-vintage', 'television-antenna',
  'cup-coffee', 'glass', 'mug', 'cookie',
]
const BATH_PROPS: readonly CatalogId[] = ['bottle', 'cup', 'glass', 'book']
const SOFT_PROPS: readonly CatalogId[] = ['pillow', 'pillow-long']

const RUGS: readonly CatalogId[] = ['rug-rectangle', 'rug-square', 'rug-oval', 'rug-round']
const PLANTS: readonly CatalogId[] = ['plant-large', 'potted-plant', 'lamp-floor', 'lamp-floor-square']
const FLOOR_BITS: readonly CatalogId[] = ['trash-bin', 'cardboard-box', 'cardboard-box-open', 'barrel', 'bag']
const WALL_DECOR: readonly CatalogId[] = [
  'lamp-wall', 'mirror', 'coat-hooks', 'bear-trophy', 'bathroom-cabinet',
  'kitchen-cabinet-upper-low', 'kitchen-cabinet-upper-corner',
]
const CEILING_ITEMS: readonly CatalogId[] = ['lamp-ceiling-square', 'ceiling-fan']
const DINING_TABLES: readonly CatalogId[] = [
  'table-dining', 'table-round', 'table-glass', 'table-cross', 'table-cloth',
]
const DINING_CHAIRS: readonly CatalogId[] = [
  'chair', 'chair-rounded', 'chair-cantilever', 'stool-bar-square', 'stool-bar',
]

/* --- 공용 그룹 --------------------------------------------------------------- */

/**
 * 어느 구획에나 붙는 마감 그룹들. 태그마다 따로 적으면 다섯 벌이 서로 어긋나기 시작한다.
 * 벽걸이를 `priority 0`에 둔 이유: 벽걸이는 **바닥을 한 칸도 쓰지 않으므로**
 * 먼저 놓아도 가구 자리를 빼앗지 않는다. 그리고 복도처럼 바닥이 좁은 구획은
 * 이 그룹이 없으면 영원히 볼 것이 없는 채로 남는다(스폰 지점이 복도다).
 */
function commonRecipes(propIds: readonly CatalogId[]): readonly GroupRecipe[] {
  return [
    {
      // 상한이 2인 이유: 태그로 걸러지고 나면 구획당 벽걸이 후보가 3~4종뿐이라
      // 더 놓으면 같은 액자가 네 벽에 걸린다. 밀도는 벽이 아니라 가구가 만든다.
      id: 'wall-decor',
      priority: 0,
      maxPerZone: 2,
      slots: [slot('decor', from(WALL_DECOR, 'wall'), wallMounted(), 1, 2)],
    },
    {
      id: 'corner-plant',
      priority: 1,
      maxPerZone: 2,
      slots: [
        slot('plant', from(PLANTS, 'floor'), atCorner(), 1, 1),
        slot('bit', from(FLOOR_BITS, 'floor'), beside('plant'), 0, 1),
        slot('prop', from(propIds, 'surface'), onSurface('bit'), 0, 1),
      ],
    },
    {
      // 우선순위 0인 이유는 밀도가 아니라 규칙성이다. **모든 방에 조명이 하나씩** 있는 것은
      // 그 자체로 술래가 기댈 수 있는 패턴이고, 천장은 바닥 예산을 한 칸도 쓰지 않는다.
      id: 'ceiling-light',
      priority: 0,
      maxPerZone: 1,
      slots: [slot('light', from(CEILING_ITEMS, 'ceiling'), onCeiling(), 1, 1)],
    },
  ]
}

/** 식탁 세트. 주방과 거실이 같은 구조를 공유한다 - 식탁이 있고 그 둘레에 의자가 있다. */
function diningRecipe(priority: number): GroupRecipe {
  return {
    id: 'dining-set',
    priority,
    maxPerZone: 1,
    slots: [
      slot('table', from(DINING_TABLES, 'floor'), atCenter(), 1, 1),
      slot('chair', from(DINING_CHAIRS, 'floor'), around('table'), 2, 4),
      slot('prop', from(TABLE_PROPS, 'surface'), onSurface('table'), 0, 3),
    ],
  }
}

/* --- 구획별 레시피 ----------------------------------------------------------- */

const BEDROOM_RECIPES: readonly GroupRecipe[] = [
  {
    // 침실의 정의. 침대가 벽에 붙고, 협탁이 그 옆에, 스탠드가 협탁 위에 - 이것이 1청크다.
    id: 'bed-set',
    priority: 0,
    maxPerZone: 1,
    slots: [
      slot('bed', from(['bed-double', 'bed-single', 'bed-bunk'], 'anchor'), againstWall(), 1, 1),
      slot('nightstand', from(['nightstand', 'nightstand-legs'], 'floor'), beside('bed'), 0, 2),
      slot('bedside', from(BEDSIDE_PROPS, 'surface'), onSurface('nightstand'), 0, 2),
      slot('pillow', from(SOFT_PROPS, 'surface'), onSurface('bed'), 0, 2),
      slot('rug', from(RUGS, 'floor'), inFront('bed', 1), 0, 1),
    ],
  },
  {
    id: 'wardrobe-set',
    priority: 0,
    maxPerZone: 1,
    slots: [
      slot('wardrobe', from(['wardrobe', 'bookcase-doors', 'bookcase-open', 'bookcase-open-low'], 'anchor'), againstWall(), 1, 1),
      slot('box', from(['cardboard-box', 'cardboard-box-open'], 'floor'), alongWall('wardrobe'), 0, 1),
      slot('prop', from(SHELF_PROPS, 'surface'), onSurface('box'), 0, 1),
      slot('hook', from(['coat-hooks', 'mirror'], 'wall'), wallMounted('wardrobe'), 0, 1),
    ],
  },
  {
    id: 'desk-set',
    priority: 1,
    maxPerZone: 1,
    slots: [
      slot('desk', from(['desk', 'desk-corner'], 'floor'), againstWall(), 1, 1),
      slot('chair', from(['chair-desk', 'chair-rounded', 'chair'], 'floor'), facing('desk'), 0, 1),
      slot('prop', from(DESK_PROPS, 'surface'), onSurface('desk'), 0, 3),
    ],
  },
  ...commonRecipes(BEDSIDE_PROPS),
]

const LIVING_RECIPES: readonly GroupRecipe[] = [
  {
    // 거실의 정의. 소파 → 커피테이블 → 그 맞은편 TV장 → TV. 한 줄로 읽히는 배치다.
    id: 'sofa-set',
    priority: 0,
    maxPerZone: 1,
    slots: [
      slot('sofa', from(['sofa', 'sofa-modern', 'sofa-chaise', 'sofa-corner'], 'anchor'), againstWall(), 1, 1),
      slot('coffee', from(['table-coffee', 'table-coffee-square', 'table-coffee-glass', 'table-coffee-glass-square'], 'floor'), inFront('sofa', 2), 0, 1),
      slot('coffee-prop', from(TABLE_PROPS, 'surface'), onSurface('coffee'), 0, 3),
      slot('tv-stand', from(['cabinet-tv', 'cabinet-tv-doors'], 'anchor'), facing('sofa', 8), 0, 1),
      slot('tv', from(['television', 'television-vintage'], 'floor'), onSurface('tv-stand'), 0, 1),
      slot('pillow', from(SOFT_PROPS, 'surface'), onSurface('sofa'), 0, 2),
    ],
  },
  {
    id: 'shelf-set',
    priority: 0,
    maxPerZone: 2,
    slots: [
      slot('shelf', from(['shelf-tall', 'bookcase-open', 'bookcase-doors', 'bookcase-open-low'], 'anchor'), againstWall(), 1, 1),
      slot('prop', from(SHELF_PROPS, 'surface'), onSurface('shelf'), 0, 2),
      slot('plant', from(PLANTS, 'floor'), alongWall('shelf'), 0, 1),
    ],
  },
  {
    id: 'armchair-set',
    priority: 1,
    maxPerZone: 2,
    slots: [
      slot('chair', from(['armchair', 'recliner', 'lounge-chair-wide', 'chair-rounded'], 'floor'), againstWall(), 1, 1),
      slot('side', from(['side-table', 'nightstand-legs', 'table-coffee-square', 'ottoman'], 'floor'), beside('chair'), 0, 1),
      slot('prop', from(TABLE_PROPS, 'surface'), onSurface('side'), 0, 2),
      slot('lamp', from(['lamp-floor', 'lamp-floor-square'], 'floor'), beside('chair', 1), 0, 1),
    ],
  },
  {
    id: 'desk-set',
    priority: 1,
    maxPerZone: 1,
    slots: [
      slot('desk', from(['desk', 'desk-corner'], 'floor'), againstWall(), 1, 1),
      slot('chair', from(['chair-desk', 'chair'], 'floor'), facing('desk'), 0, 1),
      slot('prop', from(DESK_PROPS, 'surface'), onSurface('desk'), 0, 3),
    ],
  },
  diningRecipe(1),
  ...commonRecipes(TABLE_PROPS),
]

const KITCHEN_RECIPES: readonly GroupRecipe[] = [
  {
    // 주방의 정의. 싱크대에서 시작해 한 벽을 따라 조리대가 이어지고, 그 위에 상부장이 걸린다.
    // `alongWall`이 자기 역할을 다시 참조하므로 두 번째 가구부터는 **앞 가구 옆**에 붙는다.
    id: 'counter-run',
    priority: 0,
    maxPerZone: 1,
    slots: [
      slot('counter', from(['kitchen-sink', 'kitchen-cabinet', 'kitchen-stove'], 'anchor'), againstWall(), 1, 1),
      slot('counter-run', from(['kitchen-stove', 'kitchen-cabinet', 'kitchen-cabinet-corner', 'fridge', 'fridge-large', 'fridge-built-in'], 'anchor'), alongWall('counter-run'), 1, 3),
      slot('upper', from(['kitchen-cabinet-upper', 'range-hood', 'range-hood-modern', 'kitchen-cabinet-upper-low'], 'wall'), wallMounted('counter'), 0, 2),
      slot('prop', from(KITCHEN_PROPS, 'surface'), onSurface('counter'), 0, 3),
      slot('run-prop', from(KITCHEN_PROPS, 'surface'), onSurface('counter-run'), 0, 2),
    ],
  },
  diningRecipe(0),
  {
    id: 'bar-set',
    priority: 1,
    maxPerZone: 1,
    slots: [
      slot('bar', from(['kitchen-bar', 'kitchen-bar-end'], 'floor'), againstWall(), 1, 1),
      slot('stool', from(['stool-bar', 'stool-bar-square', 'chair-cantilever'], 'floor'), facing('bar'), 0, 2),
      slot('prop', from(KITCHEN_PROPS, 'surface'), onSurface('bar'), 0, 2),
    ],
  },
  {
    id: 'appliance-set',
    priority: 1,
    maxPerZone: 1,
    slots: [
      slot('appliance', from(['fridge-small', 'washer', 'kitchen-cabinet-corner'], 'floor'), againstWall(), 1, 1),
      slot('prop', from(KITCHEN_PROPS, 'surface'), onSurface('appliance'), 0, 2),
    ],
  },
  ...commonRecipes(KITCHEN_PROPS),
]

const BATH_RECIPES: readonly GroupRecipe[] = [
  {
    id: 'bath-fixtures',
    priority: 0,
    maxPerZone: 1,
    slots: [
      slot('fixture', from(['bathtub', 'shower-booth', 'shower-booth-round'], 'anchor'), againstWall(), 1, 1),
      slot('fixture-run', from(['toilet', 'bathroom-sink', 'bathroom-sink-square', 'bathroom-vanity'], 'floor'), alongWall('fixture-run'), 1, 2),
      slot('mirror', from(['mirror', 'bathroom-cabinet'], 'wall'), wallMounted('fixture-run'), 0, 1),
      slot('prop', from(BATH_PROPS, 'surface'), onSurface('fixture-run'), 0, 2),
    ],
  },
  {
    id: 'vanity-set',
    priority: 0,
    maxPerZone: 1,
    slots: [
      slot('vanity', from(['bathroom-vanity', 'bathroom-sink-square', 'bathroom-sink'], 'floor'), againstWall(), 1, 1),
      slot('mirror', from(['mirror', 'bathroom-cabinet'], 'wall'), wallMounted('vanity'), 0, 1),
      slot('prop', from(BATH_PROPS, 'surface'), onSurface('vanity'), 0, 2),
    ],
  },
  {
    id: 'laundry-set',
    priority: 1,
    maxPerZone: 1,
    slots: [
      slot('washer', from(['washer-dryer-tower', 'washer', 'dryer'], 'anchor'), againstWall(), 1, 1),
      slot('extra', from(['dryer', 'washer'], 'floor'), alongWall('washer'), 0, 1),
      slot('prop', from(BATH_PROPS, 'surface'), onSurface('extra'), 0, 1),
    ],
  },
  ...commonRecipes(BATH_PROPS),
]

const HALL_RECIPES: readonly GroupRecipe[] = [
  {
    id: 'entry-set',
    priority: 0,
    maxPerZone: 1,
    slots: [
      slot('rack', from(['coat-rack', 'bookcase-open-low'], 'floor'), againstWall(), 1, 1),
      slot('mat', from(['doormat', 'rug-square'], 'floor'), inFront('rack', 1), 0, 1),
      slot('box', from(FLOOR_BITS, 'floor'), alongWall('rack'), 0, 1),
      slot('hook', from(['coat-hooks', 'mirror'], 'wall'), wallMounted('rack'), 0, 1),
    ],
  },
  {
    id: 'bench-set',
    priority: 0,
    maxPerZone: 1,
    slots: [
      slot('bench', from(['bench', 'bench-backrest', 'lounge-chair-wide', 'ottoman'], 'floor'), againstWall(), 1, 1),
      slot('pillow', from(SOFT_PROPS, 'surface'), onSurface('bench'), 0, 2),
      slot('side', from(['nightstand-legs', 'table-coffee-square', 'table-cross'], 'floor'), beside('bench'), 0, 1),
      slot('prop', from(TABLE_PROPS, 'surface'), onSurface('side'), 0, 2),
    ],
  },
  {
    id: 'console-set',
    priority: 1,
    maxPerZone: 1,
    slots: [
      slot('console', from(['bookcase-open-low', 'table-cross', 'nightstand-legs'], 'floor'), againstWall(), 1, 1),
      slot('prop', from(SHELF_PROPS, 'surface'), onSurface('console'), 0, 3),
      slot('decor', from(WALL_DECOR, 'wall'), wallMounted('console'), 0, 1),
    ],
  },
  ...commonRecipes(SHELF_PROPS),
]

/**
 * 집 한 채의 레시피 책. `roomGen`의 기본값이며, 다른 컨셉의 맵은 이 책만 바꿔 끼운다.
 * 키는 `floorPlan.RoomTag` 문자열이다 - 코어가 카탈로그 태그 목록을 import하지 않기 위해
 * 문자열로 둔다(없는 태그는 그 구획이 비는 것으로 조용히 처리된다).
 */
export const HOME_RECIPES: RecipeBook = new Map<string, readonly GroupRecipe[]>([
  ['bedroom', BEDROOM_RECIPES],
  ['living', LIVING_RECIPES],
  ['kitchen', KITCHEN_RECIPES],
  ['bath', BATH_RECIPES],
  ['hall', HALL_RECIPES],
])

/** 레시피가 최소한 몇 개의 오브젝트를 요구하는가. 남은 예산이 이보다 적으면 시작하지 않는다. */
export function recipeMinSize(recipe: GroupRecipe): number {
  return recipe.slots.reduce((sum, groupSlot) => sum + groupSlot.min, 0)
}
